import { useState, useEffect, useCallback } from 'react';
import { organizationSiteService } from '../services/organizationSiteService';

/**
 * Custom hook for fetching and managing organization-wide site data
 * 
 * This hook provides a React-friendly interface to the organizationSiteService,
 * handling loading states, error handling, and data caching. It follows the
 * service-oriented architecture pattern by wrapping the service with React-specific
 * state management.
 */
export const useOrganizationSites = (organizationId) => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch all sites for the organization
   * Uses useCallback to prevent unnecessary re-renders and infinite loops
   */
  const fetchSites = useCallback(async () => {
    if (!organizationId) {
      setSites([]);
      setError(null);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const sitesData = await organizationSiteService.getSitesWithDisplayMetadata(organizationId);
      setSites(sitesData);
    } catch (err) {
      console.error("Error fetching organization sites:", err);
      setError(err.message || "Failed to fetch sites");
      setSites([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  // Fetch sites when organization ID changes
  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  return { 
    sites, 
    loading, 
    error, 
    refetch: fetchSites 
  };
};

/**
 * Hook for getting organization site statistics
 * Provides summary data like total sites, active sites, etc.
 */
export const useOrganizationSiteStats = (organizationId) => {
  const [stats, setStats] = useState({
    totalSites: 0,
    activeSites: 0,
    accountCount: 0,
    sitesByAccount: {}
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    if (!organizationId) {
      setStats({
        totalSites: 0,
        activeSites: 0,
        accountCount: 0,
        sitesByAccount: {}
      });
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const statsData = await organizationSiteService.getOrganizationSiteStats(organizationId);
      setStats(statsData);
    } catch (err) {
      console.error("Error fetching organization site stats:", err);
      setError(err.message || "Failed to fetch site statistics");
      setStats({
        totalSites: 0,
        activeSites: 0,
        accountCount: 0,
        sitesByAccount: {}
      });
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { 
    stats, 
    loading, 
    error, 
    refetch: fetchStats 
  };
};